'use client';

import type { Realty } from '../../types/realty';
import type { Swiper as SwiperInstance } from 'swiper';

import { useCallback, useState } from 'react';
import Image from 'next/image';
import { A11y, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import 'swiper/css';
import 'swiper/css/navigation';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../homepage/shared/heading';
import { RealtyCard } from './realty-card';

const ACTIVE_ARROW_SRC = '/images/realty/related-carousel-arrow-active.svg';
const DISABLED_ARROW_SRC = '/images/realty/related-carousel-arrow-disabled.svg';

type RealtiesSwiperProps = {
  realties: Realty[];
  variant?: 'default' | 'related';
  heading?: string;
  prefetchFirst?: boolean;
  prefetchOnHover?: boolean;
};

type NavigationState = {
  canGoPrevious: boolean;
  canGoNext: boolean;
};

const RelatedArrow = ({
  direction,
  disabled,
}: {
  direction: 'previous' | 'next';
  disabled: boolean;
}) => (
  <span
    aria-hidden="true"
    className={cn('absolute inset-0', direction === 'previous' && 'rotate-180')}
  >
    <span className="absolute inset-[33.95%_11.92%_34%_14.58%]">
      <Image
        alt=""
        className="select-none"
        draggable={false}
        fill
        src={disabled ? DISABLED_ARROW_SRC : ACTIVE_ARROW_SRC}
        unoptimized
      />
    </span>
  </span>
);

export const RealtiesSwiper = ({
  realties,
  variant = 'default',
  heading,
  prefetchFirst = false,
  prefetchOnHover = false,
}: RealtiesSwiperProps) => {
  const { t } = useTranslations();
  const isRelatedVariant = variant === 'related';
  const [swiper, setSwiper] = useState<SwiperInstance | null>(null);
  const [navigationState, setNavigationState] = useState<NavigationState>(() => ({
    canGoPrevious: false,
    canGoNext: isRelatedVariant && realties.length > 1,
  }));

  const syncNavigationState = useCallback((instance: SwiperInstance) => {
    const isLocked = instance.isLocked || instance.slides.length <= 1;

    setNavigationState({
      canGoPrevious: !isLocked && !instance.isBeginning,
      canGoNext: !isLocked && !instance.isEnd,
    });
  }, []);

  const handleSwiper = useCallback(
    (instance: SwiperInstance) => {
      setSwiper(instance);
      syncNavigationState(instance);
    },
    [syncNavigationState],
  );

  const carousel = (
    <Swiper
      a11y={
        isRelatedVariant
          ? {
              enabled: true,
              containerMessage: heading ?? null,
              containerRole: 'region',
              nextSlideMessage: t('text3530'),
              paginationBulletMessage: `${heading ?? t('text3530')} {{index}}`,
              prevSlideMessage: t('text3529'),
              slideLabelMessage: '{{index}} / {{slidesLength}}',
            }
          : undefined
      }
      navigation={!isRelatedVariant}
      pagination={isRelatedVariant ? { clickable: true } : true}
      modules={isRelatedVariant ? [A11y, Pagination] : [Navigation, Pagination]}
      className={cn(
        'w-full min-w-0',
        'mySwiper',
        isRelatedVariant
          ? 'ea-realty-related-swiper !pb-[38px] sm:!pb-0'
          : 'ea-blog-swiper !pb-8 sm:!pb-0',
      )}
      spaceBetween={12}
      slidesPerView={isRelatedVariant ? 'auto' : 1}
      watchOverflow={true}
      onSwiper={isRelatedVariant ? handleSwiper : undefined}
      onAfterInit={isRelatedVariant ? syncNavigationState : undefined}
      onBreakpoint={isRelatedVariant ? syncNavigationState : undefined}
      onResize={isRelatedVariant ? syncNavigationState : undefined}
      onSlideChange={isRelatedVariant ? syncNavigationState : undefined}
      onUpdate={isRelatedVariant ? syncNavigationState : undefined}
      breakpoints={{
        1024: {
          spaceBetween: 24,
        },
        768: {
          spaceBetween: 16,
        },
      }}
    >
      {realties.map((realty, index) => (
        <SwiperSlide
          key={realty.name}
          className={cn(
            'w-full',
            isRelatedVariant ? 'max-w-[358px] md:max-w-[906px]' : 'max-w-none',
          )}
        >
          <RealtyCard
            realty={realty}
            key={realty.slug}
            prefetch={prefetchFirst && index === 0}
            prefetchOnHover={prefetchOnHover}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );

  if (!isRelatedVariant) return carousel;

  return (
    <div className="w-full" data-eli-component="related-realties-carousel">
      <div className="mb-8 flex min-h-[38px] w-full items-center justify-between sm:min-h-[46px]">
        {heading ? <Heading title={heading} /> : null}
        <div
          className="hidden h-8 w-20 shrink-0 items-center gap-4 sm:flex"
          data-eli-part="related-carousel-navigation"
        >
          <button
            type="button"
            aria-label={t('text3529')}
            className="relative size-8 shrink-0 overflow-hidden border-0 bg-transparent p-0 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default"
            data-eli-part="related-carousel-previous"
            disabled={!navigationState.canGoPrevious}
            onClick={() => swiper?.slidePrev()}
          >
            <RelatedArrow direction="previous" disabled={!navigationState.canGoPrevious} />
          </button>
          <button
            type="button"
            aria-label={t('text3530')}
            className="relative size-8 shrink-0 overflow-hidden border-0 bg-transparent p-0 focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default"
            data-eli-part="related-carousel-next"
            disabled={!navigationState.canGoNext}
            onClick={() => swiper?.slideNext()}
          >
            <RelatedArrow direction="next" disabled={!navigationState.canGoNext} />
          </button>
        </div>
      </div>
      {carousel}
    </div>
  );
};
